/**
 * <related-rail> — tabbed, portable collection rail.
 *
 * Only the first tab is server-rendered. Every other tab's <ul> carries
 * data-rail-lazy="<collection url>" and is filled on first hover/open by fetching
 * that collection's cards from sections/related-rail-panel.liquid (Section
 * Rendering API), then trimmed to data-rail-cards and minus data-rail-exclude (the
 * page's own product). Switching toggles [hidden]. A single progress bar + arrow
 * pair drive whichever panel is active. The pinned leading
 * panel (image / label / "View all") reflects the active tab, swapped on change.
 * Init is deferred via the theme's initLazyScript so nav measurement never runs
 * while the section is far off-screen.
 */
if (!customElements.get('related-rail')) {
  class RelatedRail extends HTMLElement {
    constructor() {
      super();
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.activeIndex = 0;
      // Nothing to show → bail. A single-tab rail (one panel, no tab strip) still inits.
      if (!this.panels.length) return;

      if (typeof window.initLazyScript === 'function') {
        window.initLazyScript(this, this.init.bind(this));
      } else {
        this.init();
      }
    }

    init() {
      this.tablist = this.querySelector('[role="tablist"]');
      this.lead = this.querySelector('[data-rail-lead]');
      this.leadBg = this.querySelector('[data-rail-lead-bg]');
      this.leadLabel = this.querySelector('[data-rail-lead-label]');
      this.leadSubtitle = this.querySelector('[data-rail-lead-subtitle]');
      this.seeAll = this.querySelector('[data-rail-see-all]');
      this.seeAllHeader = this.querySelector('[data-rail-see-all-header]');
      this.headingCollection = this.querySelector('[data-rail-heading-collection]');
      this.leadingEnabled = this.dataset.leadingPanel === 'true';
      this.prevBtn = this.querySelector('[data-rail-prev]');
      this.nextBtn = this.querySelector('[data-rail-next]');

      this.cardsPerTab = parseInt(this.dataset.railCards, 10) || 12;
      this.excludeHandle = this.dataset.railExclude || '';
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.activate(i));
        // Warm the tab on intent so the cards are usually there before the click lands.
        tab.addEventListener('pointerenter', () => this.ensureLoaded(i), { passive: true });
        tab.addEventListener('focus', () => this.ensureLoaded(i));
      });
      if (this.tablist) this.tablist.addEventListener('keydown', this.onKeydown.bind(this));

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.page(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.page(1));

      // Any panel's scroll updates the shared progress/arrows (only the active one is visible).
      this.panels.forEach((panel) => {
        const sc = panel.querySelector('.related-rail__viewport');
        if (sc) sc.addEventListener('scroll', () => this.rafUpdate(), { passive: true });
      });
      window.addEventListener('resize', () => this.rafUpdate());

      // Preload every tab's panel image so switching the banner is instant (no flash).
      if (this.leadingEnabled) {
        this.tabs.forEach((tab) => {
          const src = tab.dataset.panelImage;
          if (src) { const pre = new Image(); pre.src = src; }
        });
      }

      this.syncActiveChrome();
      this.updateNav();
      this.scheduleWarm();
    }

    /**
     * Warm the other tabs after load, one fetch at a time across every rail on the page
     * (shared queue) and only in idle time, so a tab click usually lands on cards that are
     * already in. Costs nothing on first paint: init() only runs once the rail is near the
     * viewport, and hover/click intent (ensureLoaded) still jumps the queue.
     */
    scheduleWarm() {
      // All of this rail's other tabs fetch together (3-4 small requests); rails take turns.
      const warmRail = () => Promise.all(this.tabs.map((_, i) => (i === this.activeIndex ? null : this.ensureLoaded(i))));
      const run = () => { RelatedRail.warmChain = RelatedRail.warmChain.then(warmRail); };
      if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 600 });
      else setTimeout(run, 300);
      // The pointer entering the rail is strong intent: warm now, ahead of the queue.
      // ensureLoaded de-dupes, so this never double-fetches.
      this.addEventListener('pointerenter', warmRail, { once: true, passive: true });
    }

    activeScroller() {
      const panel = this.panels[this.activeIndex];
      return panel ? panel.querySelector('.related-rail__viewport') : null;
    }

    activate(index) {
      if (index === this.activeIndex || !this.panels[index]) return;

      // The outgoing panel has been seen, so its cards' drop-in has played. Mark it so the
      // animation doesn't replay when the user comes back (hiding/showing restarts CSS
      // animations). Each panel therefore animates on its first reveal only.
      const outgoing = this.panels[this.activeIndex];
      const outTrack = outgoing && outgoing.querySelector('.related-rail__track');
      if (outTrack) outTrack.classList.add('has-animated');

      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
      });
      this.panels.forEach((panel, i) => { panel.hidden = i !== index; });
      this.activeIndex = index;
      this.ensureLoaded(index);

      const sc = this.activeScroller();
      if (sc) sc.scrollLeft = 0;
      this.syncActiveChrome();
      this.updateNav();
      if (this.tabs[index]) this.tabs[index].focus();
    }

    /**
     * Fetches a lazy tab's cards once. The track keeps its data-rail-lazy attribute
     * until the cards are in so a failed fetch can be retried on the next open.
     */
    ensureLoaded(index) {
      const panel = this.panels[index];
      const track = panel && panel.querySelector('[data-rail-lazy]');
      if (!track || track.dataset.railLoading) return Promise.resolve();
      const url = track.dataset.railLazy;
      if (!url) return Promise.resolve();
      track.dataset.railLoading = '1';
      // Hold the row at the rendered tab's height while fetching (the shimmer in
      // related-collections-rail.css reads this), so the row doesn't grow when cards land.
      const sc = this.activeScroller();
      if (sc && sc.offsetHeight) this.style.setProperty('--rail-row-h', `${sc.offsetHeight}px`);
      const sep = url.includes('?') ? '&' : '?';
      return fetch(`${url}${sep}section_id=related-rail-panel`, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`rail tab ${r.status}`))))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          let items = Array.from(doc.querySelectorAll('li.slider__item'));
          if (this.excludeHandle) {
            items = items.filter((li) => {
              const a = li.querySelector('a.js-prod-link[href]');
              const path = a ? a.getAttribute('href').split('?')[0] : '';
              return !path.endsWith(`/products/${this.excludeHandle}`);
            });
          }
          // Marker for cards that arrived by fetch. With tabs warmed in idle time the cards
          // are usually in before the click, so they keep the drop-in animation, which plays
          // when the tab is revealed.
          track.classList.add('is-lazy-loaded');
          items.slice(0, this.cardsPerTab).forEach((li) => track.appendChild(li));
          delete track.dataset.railLazy;
          delete track.dataset.railLoading;
          if (typeof window.initLazyImages === 'function') window.initLazyImages();
          // Cards usually land in a hidden panel, and browsers don't fetch lazy images in
          // hidden content, so the tab would open with empty image boxes that pop in. Warm
          // the first row's images (what's visible on desktop); the rest stay lazy.
          Array.from(track.children).slice(0, 8).forEach((li) => {
            li.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
          });
          if (index === this.activeIndex) this.updateNav();
        })
        .catch(() => { delete track.dataset.railLoading; });
    }

    // Leading panel + "View all" follow the active tab. The panel shows only when
    // enabled and the active tab has an image; when it's hidden, the header "View
    // all" fallback shows instead, so a see-all is always present.
    syncActiveChrome() {
      const tab = this.tabs[this.activeIndex];
      const img = tab ? (tab.dataset.panelImage || '') : '';
      const tabLabel = tab ? (tab.textContent || '').trim() : '';
      const panelLabel = tab ? (tab.dataset.panelLabel || tabLabel) : '';
      const subtitle = tab ? (tab.dataset.panelSubtitle || '') : '';
      const url = tab ? (tab.dataset.url || '') : '';
      const showLead = !!(this.lead && this.leadingEnabled && img);

      // Heading shows the active tab's collection name (editable via the tab label).
      if (this.headingCollection) this.headingCollection.textContent = tabLabel;

      if (this.lead) {
        if (showLead) {
          if (this.leadBg) {
            this.leadBg.style.backgroundImage = `url(${img})`;
            // Honour the image's focal point (set in Shopify admin) so the crop keeps the subject.
            this.leadBg.style.backgroundPosition = tab.dataset.panelFocal || 'center';
          }
          if (this.leadLabel) this.leadLabel.textContent = panelLabel;
          if (this.leadSubtitle) this.leadSubtitle.textContent = subtitle;
          if (this.seeAll && url) this.seeAll.setAttribute('href', url);
          this.lead.classList.toggle('related-rail__lead--dark', (tab && tab.dataset.panelText) === 'dark');
        }
        this.lead.hidden = !showLead;
      }

      // Header "See more" always follows the active tab's href. It's hidden only on
      // DESKTOP when the leading panel is showing (CSS via this class) — the panel is
      // hidden on mobile, so the header link must stay as the mobile see-more.
      this.classList.toggle('related-rail--lead-active', showLead);
      if (this.seeAllHeader && url) this.seeAllHeader.setAttribute('href', url);
    }

    onKeydown(event) {
      const { key } = event;
      let target = null;
      if (key === 'ArrowRight' || key === 'ArrowDown') target = (this.activeIndex + 1) % this.tabs.length;
      else if (key === 'ArrowLeft' || key === 'ArrowUp') target = (this.activeIndex - 1 + this.tabs.length) % this.tabs.length;
      else if (key === 'Home') target = 0;
      else if (key === 'End') target = this.tabs.length - 1;
      else return;
      event.preventDefault();
      this.activate(target);
    }

    page(direction) {
      const sc = this.activeScroller();
      if (sc) sc.scrollBy({ left: direction * sc.clientWidth * 0.9, behavior: 'smooth' });
    }

    rafUpdate() {
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = requestAnimationFrame(() => this.updateNav());
    }

    updateNav() {
      const sc = this.activeScroller();
      if (!sc) return;
      const max = sc.scrollWidth - sc.clientWidth;
      const overflows = max > 1;
      const pos = sc.scrollLeft;
      if (this.prevBtn) this.prevBtn.disabled = !overflows || pos <= 0;
      if (this.nextBtn) this.nextBtn.disabled = !overflows || pos >= max - 1;
    }
  }
  // Shared across all rails on the page: tab warming runs one fetch at a time.
  RelatedRail.warmChain = Promise.resolve();
  customElements.define('related-rail', RelatedRail);
}
